import * as THREE from 'three';
import BoxCollider from '../three-base-components/BoxCollider';
import { createAnimationByType } from './animations';
import { getKey, setKey } from '../utils/sessionStorageHelpers';
import HotspotTooltip from '@3jss/Components/HotspotTooltip';
import { isMobile } from 'react-device-detect';
import ContentTooltip from '@3jss/Components/ContentTooltip';
import { rotateCameraTowardsPoint } from '@3jss/Scene/sceneUtils';
import { CONTROL_MODES, DIST_FROM_HOTSPOT } from '@3jss/utils/constants';
import { MathUtils } from 'three';
import { HotspotsContainer } from './HotspotsContainer';

/**
 *
 * @category Hotspot
 *
 * @class
 *
 * @classdesc
 * ## Description
 *
 * This class is used to create a hotspot object. It is a wrapper layer that takes in a Visual Component like an Image or SVGSprite and adds a box collider around it. It also handles the logic for interaction events like click, hover, unhover, etc.
 * Also, responsible for controlling hotspot animations, and SFX,
 *
 * ### Animations
 * - Animations are created using creators in [Animations module]{@link module:Animations}
 * - Currently only supports animating visualObject
 * - If animations are enabled, they are played by default when the object is added to the scene {@link HotspotConstructor.addToScene}.
 * - Animations would not be played if the hotspot has been clicked during the current session and `config.visualObjectAnimationProps.disable_on_interaction=true`.
 * - Animations are stopped when clicked on the hotspot if `config.visualObjectAnimationProps.disable_on_interaction=true` @see {@link HotspotConstructor.onClick} {@link HotspotConstructor.onClick}
 *
 *
 * ### Sound Effects
 * Controlling Sound Effects
 *
 *
 * @example
 * const marker = new HotspotConstructor(SVGSprite, {...props})
 * scene.add(marker)
 */
class HotspotConstructor extends THREE.Group {
	#camera;
	#renderer;
	#scene;
	#canvas;
	#controls;
	/**
	 * Visual Object Animation Reference
	 * @type {Tween}
	 */
	#visualObjectAnimation;

	/**
	 * @param {Object} VisualInstance - ThreeJS Object that will be used as the visual object for the hotspot
	 * @param {Object} config - Configuration object for the hotspot
	 * @param {Object} config.scene - Reference to the scene
	 * @param {Object} config.renderer - Reference to the renderer
	 * @param {Object} config.camera - Reference to the camera
	 * @param {Object} config.controls - Reference to the controls
	 * @param {Object} config.imageURL - Image URL for the visual object
	 * @param {Boolean} config.focusOnClick - Focus camera on click
	 * @param {Object} config.transform - Transform matrix of the visual object
	 * @param {Object} config.collider_transform - Transform matrix of the collider object
	 * @param {Object} config.userData - Custom data provided by users
	 * @param {Object} config.iconConfig Additional configuration for icon
	 * @param {Object} config.iconConfig.showIcon Show/Hide visual object material.
	 * @param {Boolean} config.iconConfig.showIcon Show/Hide visual object material.
	 * @param {Object} config.visualObjectProps - Visual object configuration (passed to VisualInstance) Pass any props that VisualInstance accepts.
	 * @param {Object} config.renderOrder - Three.js `renderOrder` prop for VisualInstance.
	 * @param {Object} config.visualObjectAnimationProps - Visual object animation configuration, used to set up animation for `visualObject`.
	 * @param {Boolean} [config.visualObjectAnimationProps.enabled=false] - Enable visual object animation.
	 * @param {Boolean} [config.visualObjectAnimationProps.disable_on_interaction=false] - Disable animation if hotspot has been clicked. Tracks clicks only for the session.
	 */
	constructor(
		VisualInstance,
		{
			scene,
			renderer,
			visualObjectAnimationProps = { enabled: false },
			...config
		},
	) {
		super();

		/**
		 *
		 * @type {THREE.Scene}
		 * @private
		 * @name scene
		 * @desc Reference to the THREE Scene Object. Passed from Scene object construction.
		 */
		this.#scene = scene;

		/**
		 * Reference to the scene camera. Passed from Scene object construction.
		 * @type {Camera}
		 */
		this.#camera = config.camera;
		this.#canvas = config.canvas;
		this.#controls = config.controls;

		/**
		 * Reference to the Renderer. Passed from Scene object construction.
		 * @type {THREE.WebGLRenderer}
		 */
		this.#renderer = renderer;
		/**
		 * Transform for `visualObject`
		 * @type {Array}
		 */
		this.transform = config.transform;

		/**
		 * Transform for `sceneObject`
		 * @type {Array}
		 */
		this.collider_transform = config.collider_transform;

		/**
		 * Configuration for visual object animations
		 * @type {Object}
		 */
		this.visualObjectAnimationProps = visualObjectAnimationProps;

		/**
		 * userData` for the hotspot. Usually the entire hotspot object from DB.
		 * @type {Object}
		 */
		this.userData = config.userData; //custom data provided by users
		this._isHotspotConstructor = true;
		/**
		 *
		 * @type {Object}
		 * @desc Config Object for HotspotConstructor
		 *
		 * @property {Object} tooltip - Tooltip Configuration
		 * @property {Object} visualObjectProps - Object containing props to be passed to [`visualObject`]{@link HotspotConstructor#visualObject}. Refer corresponding `VisualInstance` component docs for supported props.
		 * @property {Boolean} enable_moving_visual_only - Used on AVA for moving [`visualObject`]{@link HotspotConstructor#visualObject} independent of [`sceneObject`]{@link HotspotConstructor#sceneObject}
		 * @property {Boolean} allow_to_move - Used on AVA for enabling moving [this instance]{@link HotspotConstructor}
		 * @property {Boolean} showLightHelper - Used on AVA for enabling light helpers for [this instance]{@link HotspotConstructor}
		 * @property {Boolean} focusOnClick - Set on AVA for focusing camera on click.
		 * @property {Function} onClick - `onClick` callback for this hotspot instance. Returns [userData]{@link HotspotConstructor#userData}. Used in [this.onClick]{@link HotspotConstructor#onClick}
		 * @property {Function} onHover - `onHover` callback for this hotspot instance. Returns [userData]{@link HotspotConstructor#userData}. Used in [this.onHover]{@link HotspotConstructor#onHover}
		 * @property {Function} onUnhover - `onUnhover` callback for this hotspot instance. Returns [userData]{@link HotspotConstructor#userData}. Used in [this.onUnhover]{@link HotspotConstructor#onUnhover}
		 *
		 */
		this.config = {
			enable_moving_visual_only:
				config?.visualObjectProps?.enable_moving_visual_only, //TODO: keep it under visualObjectProps
			tooltip: config.tooltip,
			tooltipConfig: config.tooltipConfig,
			visualObjectProps: config.visualObjectProps,
			allow_to_move: config?.allow_to_move || false,
			showLightHelper: config.showLightHelper,
			onClick: config.onClick,
			onHover: config.onHover,
			onUnhover: config.onUnhover,
			renderOrder: config.renderOrder,
			focusOnClick: config?.focusOnClick || false,
			visible: !(
				config.hasOwnProperty('visible') && config.visible === false
			),
			controlMode: config.controlMode,
		};
		/**
		 *
		 * @type {BoxCollider}
		 * @desc Box collider object. A THREE.Mesh instance of {@link BoxCollider}, used for collider box for the hotspot.
		 *
		 */
		this.sceneObject = new BoxCollider(
			1,
			1,
			1,
			this.onHover,
			this.onUnhover,
			this.onClick,
			'true',
		);
		this.sceneObject.name = 'boxCollider'; //needed for doubleClick
		this.attach(this.sceneObject);

		let enableDepthTest = false;
		if (['3d_scene', '3d_model_scene'].includes(scene.userData.sceneType)) {
			//natural behavior as in real world
			enableDepthTest = true;
		}

		/**
		 * @type {THREE.Object3D}
		 * @desc The visual object instance, instanced from `VisualInstance` input.
		 */
		this.visualObject = new VisualInstance({
			...{
				...config.visualObjectProps,
				onLoad: () => {
					if (config.isInSceneComponent) {
						this.setColliderTransform();
						return;
					}
					this._onVisualInstanceLoaded(
						config.visualObjectProps.onLoad,
					);
				},
			},
			enableDepthTest,
			imageURL: config.imageURL,
			rotation:
				this.userData?.props?.sprite_rotation_degree ||
				config?.visualObjectProps?.rotation,
			showIcon: config?.iconConfig.hasOwnProperty('showIcon')
				? config.iconConfig.showIcon
				: true,
		});
		this.setVisibility(this.config.visible);
		HotspotsContainer.getInstance().add(this);
	}

	/**
	 * Controls visibility of visual object using config.iconConfig.showIcon.
	 *
	 * Behavior:
	 * - If `config.iconConfig.showIcon` is set to `true`, the visual object will be visible.
	 * - If `config.iconConfig.showIcon` is set to `false`, the visual object will be hidden.
	 * - Hotspot object will remain clickable even if the visual object is hidden.
	 *
	 * @param showIcon
	 */
	setShowIcon = (showIcon) => {
		if (this?.config?.iconConfig) {
			this.config.iconConfig.showIcon = showIcon;
		}
		this.visualObject.visible = showIcon;
	};

	/**
	 * Controls visibility of the hotspot object.
	 *
	 * Behavior:
	 * - If `visible` is set to `true`, the hotspot object will be visible.
	 * - If `visible` is set to `false`, the hotspot object will be hidden.
	 * - Hotspot object will not be clickable if it is hidden.
	 *
	 * @param visible
	 */
	setVisibility = (visible) => {
		this.visible = visible;
	};

	updateImageUrl = (url) => {
		this.config.imageURL = url;
		if (this?.visualObject?.setImage) {
			this.visualObject.setImage(url);
		}
	};
	updateGLB = (url, data) => {
		// /
		if (this?.visualObject?.updateGLB) {
			this.visualObject.updateGLB(url, data);
		}
	};
	applyTexturesToMeshes = (data, url) => {
		if (this?.visualObject?.applyTexturesToMeshes) {
			this.visualObject.applyTexturesToMeshes(data);
		}
	};
	hideMeshes = (data, url) => {
		if (this?.visualObject?.hideMeshes) {
			this.visualObject.hideMeshes(data);
		}
	};
	zoomToMesh = (args) => {
		if (this?.visualObject?.zoomToMesh) {
			this?.visualObject?.zoomToMesh(args);
		}
	};
	setRotation = (rotation) => {
		if (this?.visualObject?.setRotation) {
			this?.visualObject?.setRotation(rotation);
		}
	};
	enableDragRotation = (axes) => {
		if (this?.visualObject?.enableDragRotation) {
			this?.visualObject?.enableDragRotation(axes);
		}
	};
	updateText = (text) => {
		if (this?.visualObject?.setText) {
			this.visualObject.setText(text);
		}
	};

	updateVideoUrl = (url) => {
		if (this?.visualObject?.updateVideoUrl) {
			this.visualObject.updateVideoUrl(url);
		}
	};
	getRenderedDimensions = () => {
		const dist = this.#camera.position.manhattanDistanceTo(
			this.visualObject.position,
		);
		let vFOV = THREE.MathUtils.degToRad(this.#camera.fov); // convert vertical fov to radians
		let height = 2 * Math.tan(vFOV / 2) * dist; // visible height
		let width = height * this.#camera.aspect; // visible width
		return { height, width };
	};

	_onVisualInstanceLoaded = (cb) => {
		if (!this.visualObject) {
			return;
		}
		this.renderOrder = this.config.renderOrder;
		this.visualObject.renderOrder = this.config.renderOrder;

		// Adds renderOrder to children that are group. Useful for 3d_objects
		this.visualObject.traverse((child) => {
			if (child.isGroup) {
				child.renderOrder = this.config.renderOrder;
			}
		});

		// TODO: Make Dynamic
		this.visualObject.frustumCulled = false;
		this.visualObject.name = 'visualObject'; //needed for doubleClick

		//Visibility type of visual objects on different kinds of scenes
		this.visualObject.uuid
			? this.attach(this.visualObject)
			: console.error('Visual Object could not be initialized');

		this.initTransformsAndSPR();

		this._createTooltip();

		cb && cb();
	};

	/**
	 * Creates hotspot tooltip using config.tooltip and config.tooltipConfig
	 * @private
	 */
	_createTooltip = () => {
		if (this?.config?.tooltip?.enabled !== true) {
			return;
		}

		this.tooltipObject = new HotspotTooltip(
			this.config.tooltip,
			this.config.tooltipConfig,
			() => this.config.onClick(this.userData.props),
		);

		if (this.visualObject) {
			this.tooltipObject.tooltipSceneObject.position.copy(
				this.visualObject.position,
			);
		} else {
			this.tooltipObject.tooltipSceneObject.position.copy(
				this.sceneObject.position,
			);
		}

		this.attach(this.tooltipObject.tooltipSceneObject);
		this.tooltipObject.mount();
	};

	_triggerTooltip = (origin) => {
		if (this.tooltipObject?.initialized) {
			this.tooltipObject.setTooltipStatus(true, origin);
		}
	};

	_createContentTooltip = (type) => {
		const dimensions = this.getRenderedDimensions();
		this.contentTooltip = new ContentTooltip(
			{
				active: true,
				dimensions,
			},
			this.#scene.tooltipComponents[type],
			{ ...this.userData?.props },
			this.#canvas,
		);
		this.contentTooltip.tooltipSceneObject.position.copy(
			this.visualObject.position,
		);
		this.attach(this.contentTooltip.tooltipSceneObject);
		this.contentTooltip.mount();
	};

	_triggerContentTooltip = (type) => {
		if (this.contentTooltip) {
			this.contentTooltip.setTooltipStatus(true);
		} else {
			this._createContentTooltip(type);
		}
	};

	/**
	 * Adds this to scene
	 * @method
	 * @type Function
	 * */
	addToScene = (scene) => {
		if (!this.sceneObject)
			console.error('addToScene: no sceneObject!!! yet', this);
		this.#scene = scene;
		this.#scene.add(this);
		this.visualObjectAnimationProps.enabled &&
			this.setupVisualObjectAnimation();
	};

	setupVisualObjectAnimation = () => {
		if (this.visualObjectAnimationProps.enabled) {
			/** Do not play if hotspot has been interacted with and  this.visualObjectAnimationProps.disable_on_interaction === true*/
			const hasBeenClicked = getKey(
				`clicked-${this.userData._id.$oid}`,
				false,
			);
			if (
				hasBeenClicked &&
				this.visualObjectAnimationProps.disable_on_interaction
			) {
				return;
			}
			this.#visualObjectAnimation = createAnimationByType(
				this.visualObject,
				this.visualObjectAnimationProps,
			);

			if (this.#visualObjectAnimation) {
				this.#visualObjectAnimation.start();
			}
		}
	};

	/**
	 * Scales, Position, Rotation
	 * rotateObjectToCamera require visualObject defined => call this method manually when ready
	 * @method
	 */
	initTransformsAndSPR = () => {
		/**
		 * If true print this
		 * */
		if (
			this.collider_transform?.length === 16 &&
			this.transform?.length === 16
		) {
			this.setTransform(this.collider_transform, this.transform);
		}
	};

	/**
	 * onHover Callback function. Fired from MouseListener.
	 * - If: `this.visualObject` is a group (THREE.Group) calls `this.visualObject.onHover`
	 * - Else: Calls the callback function [onHover]{@link HotspotConstructor#config} and passes {@link HotspotConstructor#userData} to it.
	 * @method
	 * @returns {HotspotConstructor#userData}
	 * */
	onHover = () => {
		/**
		 * Trigger hotspot tooltip on hover if specified in config
		 */
		if (!this.visible) return;

		if (
			this.tooltipObject &&
			this.config.tooltipConfig?.desktop_type?.includes('hover')
		) {
			this._triggerTooltip('hover');
		}

		const hsType = this.userData?.props?.hotspot_type;
		if (
			this.#scene.tooltipComponents &&
			hsType in this.#scene.tooltipComponents
		) {
			this._triggerContentTooltip(hsType);
		}

		this.config?.onHover && this.config.onHover(this.userData);
		if (hsType === 'embedded_video') {
			this.visualObject.onHover();
		}
	};

	/**
	 * onUnhover Callback function. Fired from MouseListener.
	 * - If: `this.visualObject` is a group (THREE.Group) calls `this.visualObject.onUnhover`
	 * @method
	 * @returns {HotspotConstructor#userData}
	 * */
	onUnhover = () => {
		if (!this.visible) return;

		this.contentTooltip && this.contentTooltip.setTooltipStatus(false);

		this.config?.onUnhover && this.config.onUnhover(this.userData);
		const hsType = this.userData?.props?.hotspot_type;

		if (hsType === 'embedded_video') {
			this?.visualObject?.onUnhover();
		}
	};

	/**
	 * @description
	 * onClick Callback function.
	 * - Fired from MouseListener.
	 * - Also handles disabling animations onClick.
	 * - If: `this.visualObject` is a group (THREE.Group) calls `this.visualObject.onClick`
	 * - Else: Calls the callback function [onClick]{@link HotspotConstructor#config} and passes {@link HotspotConstructor#userData} to it.
	 * @method
	 * @returns {HotspotConstructor#userData}
	 * */
	onClick = (focusRequested = false) => {
		if (!this.visible) return;

		/** Track hotspot clicked if it needs to disable animations on interaction */
		if (
			this?.visualObjectAnimationProps?.enabled &&
			this?.visualObjectAnimationProps?.disable_on_interaction &&
			this.#visualObjectAnimation?.isPlaying()
		) {
			setKey(`clicked-${this.userData._id.$oid}`, true);
			this.#visualObjectAnimation.stop();
		}

		/**
		 * Trigger hotspot tooltip on click if specified in config
		 */
		if (
			(isMobile &&
				this.config.tooltipConfig?.mobile_type?.includes('tap')) ||
			(!isMobile &&
				this.config.tooltipConfig?.desktop_type?.includes('click'))
		) {
			this._triggerTooltip('click');
		}

		if (this.config.focusOnClick || focusRequested) {
			this.focus().then(() => {
				this._onClickActions();
			});
		} else {
			this._onClickActions();
		}
	};

	_onClickActions = () => {
		const hsType = this.userData?.props?.hotspot_type;

		if (hsType === 'embedded_video') {
			this.visualObject.onClick(this.config.onClick);
		} else {
			// if a custom hotspot and the field viewpoint_id exists (click to navigate functionality #14688)
			const isNavMarker = this.userData?.type === 'NavMarker';

			const lookInDirection = !isNavMarker;
			const resetY = isNavMarker;
			const overrideOffset = isNavMarker ? 0 : undefined;

			if (this.config.controlMode === CONTROL_MODES.CLICK_NAVIGATION) {
				this.navigateToHotspot(
					lookInDirection,
					resetY,
					overrideOffset,
				).then(() => {
					this.config.onClick && this.config.onClick();
				});
			} else {
				this.config.onClick && this.config.onClick();
			}
		}
	};

	navigateToHotspot = async (
		lookInDirection = true,
		resetY = false,
		overrideOffset = undefined,
	) => {
		const hotspotWorldPosition = this.sceneObject.getWorldPosition(
			new THREE.Vector3(),
		);
		const distanceFromHotspot =
			overrideOffset !== undefined
				? overrideOffset
				: this.userData.props?.data?.distanceFromHotspot ||
				  DIST_FROM_HOTSPOT;
		let hpBoxDirection = this.sceneObject
			.getWorldDirection(new THREE.Vector3())
			.normalize();
		const offset = hpBoxDirection.multiplyScalar(distanceFromHotspot);
		const adjustedPos = hotspotWorldPosition.clone().add(offset);
		const movements = [
			this.#controls.moveTo(
				adjustedPos.x,
				resetY ? 0 : adjustedPos.y,
				adjustedPos.z,
				true,
			),
		];

		if (lookInDirection) {
			movements.push(
				this.#controls.lookInDirectionOf(
					hotspotWorldPosition.x,
					hotspotWorldPosition.y,
					hotspotWorldPosition.z,
					true,
				),
			);
		}

		if (resetY) {
			movements.push(
				this.#controls.rotatePolarTo(MathUtils.degToRad(90), true),
			);
		}

		await Promise.all(movements);
	};

	focus = () => {
		return rotateCameraTowardsPoint(
			this.visualObject.position,
			this.#camera,
			this.#controls,
		);
	};

	setTransform(colliderTransform, visualTransform) {
		// console.log('>>HOTSPOT setTransform','color:green',{colliderTransform, visualTransform});
		if (!colliderTransform || !visualTransform || !this.visualObject?.uuid)
			return;

		const colliderMatrix = new THREE.Matrix4().fromArray(colliderTransform);
		this.sceneObject.setTransform(colliderMatrix);

		this.visualObject.matrix = new THREE.Matrix4().fromArray(
			visualTransform,
		);
		this.visualObject.matrix.decompose(
			this.visualObject.position,
			this.visualObject.quaternion,
			this.visualObject.scale,
		);
	}

	setColliderTransform = () => {
		const colliderMatrix = new THREE.Matrix4().fromArray(
			this.config.visualObjectProps.collider_transform,
		);
		this.sceneObject.setTransform(colliderMatrix);
	};

	dispose = () => {
		// Stop animations
		if (
			this.#visualObjectAnimation &&
			this.#visualObjectAnimation.isPlaying()
		) {
			this.#visualObjectAnimation.stop();
		}

		//Better to keep all disposal calls in one place then check every scene element dispose fn
		for (let i = this.children.length - 1; i >= 0; i--) {
			const obj = this.children[i];
			if (obj?.geometry) {
				obj?.geometry?.dispose(); // dispose geometry
			}
			if (obj?.material) {
				// a mesh's material may be an array
				if (Array.isArray(obj.material))
					obj.material.forEach((mtl) => {
						mtl.dispose();
					});
				else obj.material.dispose();
			}

			if (obj.dispose) {
			}
			obj.dispose();

			this.remove(obj); //remove obj from HP object
			this.#scene.remove(obj); //remove obj from scene
		}

		// I'm the magic trick that removes CSS2DObject! By some reason loop above not doing so
		this.remove(this.visualObject);

		this.visualObject?.dispose();

		this.#scene.remove(this);
		if (this.tooltip?.tooltipSceneObject)
			this.#scene.remove(this.tooltip.tooltipSceneObject);

		// console.log('%c CMP Hotspot2 dispose obj', 'color:red', { this:this });
		this.visualObject = null;
		this.sceneObject = null;
	};
}

export default HotspotConstructor;
